import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, HelpCircle, Calculator, MessageSquare } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldCls = "h-11 rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";
const empty = { name: "", email: "", phone: "", make: "", model: "", year: "", topic: "quote", message: "" };
const TOPICS = [
  { value: "quote", label: "Get a quote", icon: Calculator },
  { value: "question", label: "Ask a question", icon: HelpCircle },
  { value: "other", label: "Something else", icon: MessageSquare },
];

const Label = ({ children }) => <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">{children}</label>;

const EnquiryForm = () => {
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Please enter your name, email and message."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/questions`, f);
      toast.success("Message sent!", { description: `Reference ${data.reference}. We've emailed you a copy and will reply within 24 hours.`, duration: 8000 });
      setF(empty);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="question-form">
      <Label>What can we help with?</Label>
      <div className="grid grid-cols-3 gap-2">
        {TOPICS.map(({ value, label, icon: Icon }) => (
          <button key={value} type="button" onClick={() => setF({ ...f, topic: value })}
            className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center text-[13px] font-semibold transition-colors ${f.topic === value ? "border-brand bg-brand/10 text-white" : "border-white/10 bg-[#0b0c0f] text-white/65 hover:border-white/25"}`}
            data-testid={`question-topic-${value}`}>
            <Icon className={`h-4 w-4 ${f.topic === value ? "text-brand" : "text-white/45"}`} />{label}
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div><Label>Full Name *</Label><Input className={fieldCls} placeholder="John Smith" value={f.name} onChange={set("name")} data-testid="q-input-name" /></div>
        <div><Label>Email *</Label><Input type="email" className={fieldCls} placeholder="john@email.com" value={f.email} onChange={set("email")} data-testid="q-input-email" /></div>
      </div>
      <div className="mt-5"><Label>Phone</Label><Input className={fieldCls} placeholder="07123 456789" value={f.phone} onChange={set("phone")} data-testid="q-input-phone" /></div>
      <div className="mt-5 grid grid-cols-3 gap-4">
        <div><Label>Make</Label><Input className={fieldCls} placeholder="BMW" value={f.make} onChange={set("make")} data-testid="q-input-make" /></div>
        <div><Label>Model</Label><Input className={fieldCls} placeholder="335i" value={f.model} onChange={set("model")} data-testid="q-input-model" /></div>
        <div><Label>Year</Label><Input className={fieldCls} placeholder="2015" value={f.year} onChange={set("year")} data-testid="q-input-year" /></div>
      </div>
      <div className="mt-5">
        <Label>Your Message *</Label>
        <Textarea rows={5} maxLength={2000} className={`${fieldCls} h-auto`} value={f.message} onChange={set("message")} data-testid="q-input-message"
          placeholder={f.topic === "quote" ? "Which service are you interested in? Any modifications already fitted?" : "Ask us anything about tuning, diagnostics or your vehicle..."} />
      </div>
      <button type="submit" disabled={loading} className="btn-red mt-7 flex w-full items-center justify-center gap-2 rounded-lg py-4 text-base font-semibold disabled:opacity-70" data-testid="submit-question">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {loading ? "Sending..." : f.topic === "quote" ? "Request My Quote" : "Send Message"}
      </button>
      <p className="mt-3 text-center text-[12px] text-white/40">No booking or payment. We'll simply get back to you.</p>
    </form>
  );
};

export default EnquiryForm;
